import { type IUserDetails } from 'interfaces/user';
import apiService from 'services/api';
import authSessionService from 'services/authSession';
import { ACCOUNT_ENV } from 'settings';

import {
  type AcpoAccount,
  type LoginAsEmail,
  type SendHelpRequest,
  type User,
  type UserEmailResponse,
  type WebchatSession
} from './types';

export class UserService {
  public getUser(): User | null {
    return authSessionService.getSession().user;
  }

  public setUser(user: User): User {
    return authSessionService.setUser(user) as User;
  }

  public logout(): void {
    authSessionService.beginLogout();
    authSessionService.clearSession();
    localStorage.clear();
    sessionStorage.clear();
    (window as any).Eduzz.Accounts.logout({
      ...ACCOUNT_ENV,
      redirectTo: window.location.origin
    });
  }

  public setMeAsProducer(data: any): Promise<any> {
    return apiService.post('/user/set_as_producer', data);
  }

  public hasUsedTour(data: any): Promise<any> {
    return apiService.put('/user/tour', data);
  }

  public markRegistryModalViewed(): Promise<void> {
    return apiService.post('/user/eduzz_registry/modal', {}).then(() => undefined);
  }

  public loginByEventHash(data: any): Promise<any> {
    return apiService.post('/auth/loginbyeventhash', data).then(res => res.data[0]);
  }

  public loginAs(data: any): Promise<User> {
    return apiService.post('/user/login_as', data).then(res => res.data[0]);
  }

  public sendHelp(helpData: SendHelpRequest): Promise<any> {
    const { eventId, ...data } = helpData;
    return apiService.post(`help_services/helpsend/${eventId}`, data);
  }

  public loginAsEmail(email: string): Promise<LoginAsEmail> {
    return apiService.post('/user/acpo_login_as', { email }).then(res => res.data[0]);
  }

  public getDetails(): Promise<IUserDetails> {
    return apiService.get('/user/details').then(res => res.data);
  }

  public getUserDetailByEmail(email: string): Promise<UserEmailResponse> {
    return apiService.post<{ data: UserEmailResponse[] }>('user/detail_by_email', { email }).then(res => res.data[0]);
  }

  public getAcpoAccounts(): Promise<AcpoAccount[]> {
    return apiService.get('/user/acpo_accounts').then(res => res.data);
  }

  public generateWebchatSession(): Promise<{ data: WebchatSession[] }> {
    return apiService.post('/user/support-chat/generate-webchat-session', {});
  }
}

const userService = new UserService();
export default userService;
